import React, { Component } from 'react';
import moment from 'moment';
import Form from '../form';
import Input from '../input';
import InputNumber from '../input-number';
import Select from '../select';
import DatePicker from '../date-picker';
import TreeSelect from '../tree-select';
import Radio from '../radio';
import Checkbox from '../checkbox';
import { DragableBodyRow } from './dragTable';

const FormItem = Form.Item;
const EditableContext = React.createContext(null);
const { TextArea } = Input;
const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const getColumnMapFromChildren = (children = []) => {
  const columnMap = {};
  children.forEach((child) => {
    const { column = {} } = child.props || {};
    columnMap[column.dataIndex] = column.editType || 'text';
  });
  return columnMap;
};

const Editablerow = ({ form, index, moveRow, ...props }) => {
  const columnMap = getColumnMapFromChildren(props.children);
  return (
    <EditableContext.Provider value={{ form, columnMap }}>
      {moveRow ? (
        <DragableBodyRow index={index} moveRow={moveRow} {...props} />
      ) : (
        <tr {...props} />
      )}
    </EditableContext.Provider>
  );
};

export const EditableFormRow = Form.create()(Editablerow);

interface ERProps {
  width: string | number;
  editing: boolean;
  dataIndex: string;
  title: string;
  editType: string;
  editRules: object[];
  editOptions: object[];
  editInitValue: (value: string) => string;
  record: object;
  keyValue: string;
  toggleEditRow: (key?: string, record?: object, method?: string) => void;
  editContent: string | React.ReactNode;
  saveContent: string | React.ReactNode;
  cancelContent: string | React.ReactNode;
  handleSave: (record: object, type: string) => void;
}

export class EditableRow extends Component<ERProps> {
  state = {
    relastionSelectParentValue: this.props.record?.[this.props.dataIndex],
  }

  dateTimePickerChange = (date, dateString, dataIndex) => {
    if (this.form) {
      this.form.setFieldsValue({
        [dataIndex]: dateString,
      });
    }
  };

  componentDidUpdate() {
    if (this.props.relationKey && this.form) {
      const values = this.form.getFieldsValue();
      if (this.state.relastionSelectParentValue !== values[this.props.dataIndex]) {
        this.form.setFieldsValue({
          [this.props.relationKey]: this.state.relastionSelectParentValue ? '' : values[this.props.relationKey],
        });
        this.setState({
          relastionSelectParentValue: values[this.props.dataIndex]
        })
      }
    }
  }

  getInputComponent = (type, options = [], width, dataIndex, value) => {
    if (typeof type === 'function') {
      // 自定义编辑类型
      return type();
    } else {
      switch (type) {
        case 'number':
          return <InputNumber />;
        case 'date':
          return <DatePicker />;
        case 'datetime':
          return (
            <div>
              <DatePicker
                defaultValue={moment(value, DATE_TIME_FORMAT)}
                showTime
                onChange={(date, dateString) =>
                  this.dateTimePickerChange(date, dateString, dataIndex)
                }
              />
            </div>
          );
        case 'select':
          return <Select options={options} />;
        case 'textarea':
          return <TextArea />;
        case 'text':
          return <Input />;
        case 'radio':
          return <Radio.Group options={options} />;
        case 'checkbox':
          return <Checkbox.Group options={options} />;
        case 'treeSelect':
          return (
            <TreeSelect
              showSearch
              allowClear
              labelInValue
              style={{ width }}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeData={options}
              placeholder="Please select"
              treeDefaultExpandAll
            />
          );
        case 'multiTreeSelect':
          return (
            <TreeSelect
              multiple
              showSearch
              labelInValue
              allowClear
              style={{ width }}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeData={options}
              placeholder="Please select"
              treeDefaultExpandAll
            />
          );
        default:
          break;
      }
    }
  };

  getInitValue = (type, value, initValue) => {
    if (typeof type === 'function' && typeof initValue === 'function') {
      return initValue(value);
    } else {
      switch (type) {
        case 'number':
          return Number(value);
        case 'date':
          if (value) {
            return moment(value, DATE_FORMAT);
          }
          return '';
        case 'datetime':
          return moment(value, DATE_TIME_FORMAT);
        case 'select':
        case 'text':
        case 'textarea':
          return value;
        case 'treeSelect':
          if (value) {
            // return { label: '', value }
            if (value.value) {
              return value;
            }
            return { label: '', value };
          }
          return null;
        case 'multiTreeSelect':
          if (value && Array.isArray(value)) {
            // return value.map(v => v.value)
            return value;
          }
          return null;
        default:
          return value;
      }
    }
  };

  save(form, columnMap) {
    const { record, handleSave, toggleEditRow } = this.props;
    form.validateFields((error, values) => {
      if (error) {
        return;
      }
      // eslint-disable-next-line no-unused-vars
      for (const v in values) {
        if (values[v] instanceof moment) {
          const type = (columnMap && columnMap[v]) || 'date';
          values[v] = values[v].format(
            type === 'datetime' ? DATE_TIME_FORMAT : DATE_FORMAT
          );
        }
      }
      toggleEditRow();
      handleSave({ ...record, ...values }, 'save');
    });
  }

  cancel() {
    const { record, handleSave, toggleEditRow } = this.props;
    toggleEditRow();
    handleSave(record, "cancel");
  }

  handleRender = (editType: string, restProps: any) => {
    if (editType === 'checkbox') {
      return String(restProps.children.filter((v) => v));
    }
    return restProps.children;
  };

  render() {
    const {
      width,
      editing,
      dataIndex,
      title,
      editType,
      editRules,
      editOptions,
      editInitValue,
      record,
      keyValue,
      toggleEditRow,
      editContent,
      saveContent,
      cancelContent,
      ...restProps
    } = this.props;
    if (dataIndex === 'edit') {
      return (
        <td>
          {editing ? (
            <span>
              <EditableContext.Consumer>
                {({ form, columnMap }) => (
                  <React.Fragment>
                    <a onClick={() => this.save(form, columnMap)} style={{ marginRight: 8 }}>
                      {saveContent}
                    </a>
                    <a onClick={() => this.cancel()}>{cancelContent}</a>
                  </React.Fragment>
                )}
              </EditableContext.Consumer>
            </span>
          ) : (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a onClick={() => toggleEditRow(keyValue, record, 'edit')}>{editContent}</a>
          )}
          &nbsp;&nbsp;
          {restProps.children}
        </td>
      );
    } else {
      return (
        <EditableContext.Consumer>
          {({ form }) => {
            const { getFieldDecorator } = form;
            this.form = form;
            return (
              <td>
                {editing ? (
                  <FormItem style={{ margin: 0 }}>
                    {getFieldDecorator(dataIndex, {
                      ...(editRules && editRules.length
                        ? { rules: editRules }
                        : {}),
                      initialValue: this.getInitValue(
                        editType,
                        record[dataIndex],
                        editInitValue
                      ),
                    })(
                      this.getInputComponent(
                        editType,
                        editOptions,
                        width,
                        dataIndex,
                        record[dataIndex]
                      )
                    )}
                  </FormItem>
                ) : (
                  this.handleRender(editType, restProps)
                )}
              </td>
            );
          }}
        </EditableContext.Consumer>
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/table/editRow.tsx